import React from 'react';
import LayoutTabsContent from './LayoutTabsContent';
import { useRef, useEffect, useState } from 'react';
import { useAppSelector } from '@/redux/store';
import useFilteredMessages from '@/hooks/V3/useFilteredMessage';
import { useToast } from '@/hooks/useToast';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import { cplatApiV2Sellkey } from '@/api';
import { logEvent } from '@/etc/firebase';
import { Message, ProductName } from '@/types/discover';
import BubbleContainer from '@/pages/Lab/_components/Bubble/BubbleContainer';
import BotBubble from '@/pages/Lab/_components/Bubble/BotBubble';
import Input from '@/components/atoms/Input';
import { cn } from '@/utils/tailwind';
import { useChatHandleKeyDown } from '@/hooks/V3/useChatHandleKeyDown';

interface ChangeNameDashboardProps {
  activeTab: string;
}

const ChangeNameDashboard = ({ activeTab }: ChangeNameDashboardProps) => {
  const { CplatToken, UserNo } = useAppSelector((state) => state.user);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { messages, setMessages, setStoredMessages } = useFilteredMessages(
    `change_name_messages_${UserNo}`,
    {
      id: 0,
      text: '도매몰 또는 해외 상품명을 붙여넣어보세요.\nAI가 알맞은 상품명으로 바꿔드려요.',
      sender: 'bot',
    },
  );

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isActive = !isLoading && input.trim();
  const { toast } = useToast();
  const [productNameListData, setProductNameListData] = useState<Array<ProductName>>([]);

  const botMessages = messages.filter((message) => message.sender === 'bot');
  const latestBotMessageId =
    botMessages.length > 0 ? Math.max(...botMessages.map((msg) => msg.id)) : null;

  const { setUserEventHistory } = useLog(UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const getProductName = async () => {
    const chatMessage = input.replace('\n', '');

    try {
      setIsLoading(true);
      const {
        data: { code, data },
      } = await cplatApiV2Sellkey.post('sellkey/get_optimization_product_name', {
        CplatToken: CplatToken,
        ProductName: chatMessage,
      });

      if (code === '200') {
        const { ProductNameList } = data[0];
        setProductNameListData(ProductNameList);
        const botMessage: Message = {
          id: Date.now() + 1,
          text: ProductNameList[0].product_name,
          sender: 'bot',
          subText: ProductNameList[0].Consideration,
          list: ProductNameList[0].Keywords,
          remainCount: 4,
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
        logEvent('ai_change_name', {
          status: code,
          user_id: UserNo,
          input: chatMessage,
          optimized_product_name: ProductNameList[0].product_name,
          keywords: ProductNameList[0].Keywords.join(', '),
        });
      } else {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        toast({
          icon: '⚠️',
          title: '일시적으로 오류가 발생하였습니다.\n오류가 반복되면 1:1 문의를 남겨주세요.',
          variant: 'dark',
        });
        logEvent('ai_change_name', {
          user_id: UserNo,
          input: chatMessage,
          status: code,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (isActive) {
      const newMessage: Message = { id: Date.now(), text: input, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      setInput('');
      getProductName();

      setUserEventHistory({
        eventName: 'change_ai_product_name',
        eventValue: input,
        deviceUuid,
      });
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const cleanedInput = e.target.value.replace(/ {2,}/g, ' ');
    setInput(cleanedInput);
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current && messagesEndRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'changeName') {
      scrollToBottom();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!scrollContainerRef.current || !messagesEndRef.current) return;
    setStoredMessages(messages);
    requestAnimationFrame(scrollToBottom);
  }, [messages]);

  useEffect(() => {
    if (!isLoading) return;
    if (!scrollContainerRef.current || !messagesEndRef.current) return;
    requestAnimationFrame(scrollToBottom);
  }, [isLoading]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const resize = () => {
      textarea.style.height = 'auto';
      const maxHeight = 130;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    };

    // input 값이 변경되었을 때도 resize
    resize();

    textarea.addEventListener('input', resize);
    return () => textarea.removeEventListener('input', resize);
  }, [input, activeTab]);

  const { handleKeyDown, handleCompositionStart, handleCompositionEnd } = useChatHandleKeyDown({
    sendMessage: () => {
      if (isActive) handleSendMessage(); // `isActive` 확인 후 실행
    },
    isMobile: false,
  });

  return (
    <LayoutTabsContent>
      <article ref={scrollContainerRef} className="scrollbar flex flex-1 flex-col overflow-auto  pb-[10px] ">
        {messages.map((message) => (
          <BubbleContainer
            key={message.id}
            data={message}
            messages={messages}
            setMessages={setMessages}
            setIsLoading={setIsLoading}
            productNameList={productNameListData}
            isLatest={message.sender === 'bot' && message.id === latestBotMessageId}
            actionType="default"
            isDashboard={true}
            isGradient
          />
        ))}
        <div ref={messagesEndRef} />
        {isLoading && <BotBubble type="loading" isDashboard isGradient />}
      </article>
      <div className="flex py-[10px] ">
        <Input
          rows={1}
          textarea={true}
          ref={inputRef}
          className={cn(
            'resize-none overflow-y-auto rounded-[8px] border-none bg-gr-50 py-[10px] pr-[70px] Body5M14 scrollbar-hide  focus:border-none',
          )}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          placeholder="최적화할 상품명을 입력해 주세요"
          InputProps={{
            endAdornment: (
              <button
                onClick={handleSendMessage}
                className={cn(
                  'rounded-[8px]  bg-point-500 px-[8px] py-[5px] text-white Caption1M12',
                  !isActive && 'bg-gr-300',
                )}
              >
                만들기
              </button>
            ),
            endAdornmentStyle: 'right-[8px] absolute m-auto whitespace-nowrap bottom-[7px]',
          }}
          maxLength={100}
        />
      </div>
    </LayoutTabsContent>
  );
};

export default ChangeNameDashboard;
